import {endOfDay, startOfDay, subDays, subHours, subMinutes} from "date-fns";

import {StandardDateRange} from "@/lib/queries/reports.ts";
import {SLA_REPORT_STANDARD_DATE_RANGE_OPTIONS, StandardDateRangeLabels} from "@/lib/types/reports.ts";

// If you are using these, make sure that you are handling the timezone correctly.
// it is recommended you use TZDate objects to ensure timezone correctness.
export interface DateRangeOption {
  label: string;
  value: StandardDateRange;
  getStartDate: (date: Date) => Date;
  getEndDate: (date: Date) => Date;
}

export const Last90Days: DateRangeOption = {
  label: StandardDateRangeLabels[StandardDateRange.LAST_90_DAYS],
  value: StandardDateRange.LAST_90_DAYS,
  getStartDate: (date: Date) => subDays(startOfDay(date), 90 - 1),
  getEndDate: (date: Date) => endOfDay(date),
};

export const Last30Days: DateRangeOption = {
  label: StandardDateRangeLabels[StandardDateRange.LAST_30_DAYS],
  value: StandardDateRange.LAST_30_DAYS,
  getStartDate: (date) => subDays(startOfDay(date), 30 - 1),
  getEndDate: (date) => endOfDay(date),
};

export const Last7Days: DateRangeOption = {
  label: StandardDateRangeLabels[StandardDateRange.LAST_7_DAYS],
  value: StandardDateRange.LAST_7_DAYS,
  getStartDate: (date: Date) => subDays(startOfDay(date), 7 - 1),
  getEndDate: (date: Date) => endOfDay(date),
};

export const Last24Hours: DateRangeOption = {
  label: StandardDateRangeLabels[StandardDateRange.LAST_24_HOURS],
  value: StandardDateRange.LAST_24_HOURS,
  getStartDate: (date) => subDays(date, 1),
  getEndDate: (date) => date,
};

export const DATEPICKER_STANDARD_DATE_RANGE_OPTIONS: DateRangeOption[] = [
  {
    label: StandardDateRangeLabels[StandardDateRange.LAST_30_MINUTES],
    value: StandardDateRange.LAST_30_MINUTES,
    getStartDate: (date: Date) => subMinutes(date, 30),
    getEndDate: (date: Date) => date,
  },
  {
    label: StandardDateRangeLabels[StandardDateRange.LAST_1_HOUR],
    value: StandardDateRange.LAST_1_HOUR,
    getStartDate: (date: Date) => subHours(date, 1),
    getEndDate: (date: Date) => date,
  },
  {
    label: StandardDateRangeLabels[StandardDateRange.LAST_4_HOURS],
    value: StandardDateRange.LAST_4_HOURS,
    getStartDate: (date: Date) => subHours(date, 4),
    getEndDate: (date: Date) => date,
  },
  {
    label: StandardDateRangeLabels[StandardDateRange.LAST_12_HOURS],
    value: StandardDateRange.LAST_12_HOURS,
    getStartDate: (date: Date) => subHours(date, 12),
    getEndDate: (date: Date) => date,
  },
  Last24Hours,
  {
    label: StandardDateRangeLabels[StandardDateRange.YESTERDAY],
    value: StandardDateRange.YESTERDAY,
    getStartDate: (date) => startOfDay(subDays(date, 1)), // start of day gets 00:00:00
    getEndDate: (date) => endOfDay(subDays(date, 1)),
  },
  Last7Days,
  {
    label: StandardDateRangeLabels[StandardDateRange.LAST_14_DAYS],
    value: StandardDateRange.LAST_14_DAYS,
    getStartDate: (date) => subDays(startOfDay(date), 14 - 1),
    getEndDate: (date) => date,
  },
  Last30Days,
  Last90Days,
];

export const getDatepickerStandardRangeOption = (range?: string | null) =>
  DATEPICKER_STANDARD_DATE_RANGE_OPTIONS.find((o) => o.value === range);

export const getReportsStandardRangeOption = (range?: string | null) =>
  SLA_REPORT_STANDARD_DATE_RANGE_OPTIONS.find((o) => o.value === range);
